import { Box, Button, Card, CardContent, Container, IconButton, MenuItem, Stack, Tab, Table, TableBody, TableContainer, TableHead, TableRow, Tabs, Typography, CircularProgress, alpha } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldOrganizationSettings, Organization } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import { useDropzone } from "react-dropzone";

import PageHeader from "src/components/page-header";
import useModelSavingData from "src/hooks/use-model-saving-data";
import { usePrevious } from "src/hooks/use-previous";
import Iconify from "src/components/iconify/iconify";
import TruxTextField from "src/components/inputs/trux-text-field";
import TruxSelectField from "src/components/inputs/trux-select-field";
import SimpleDialog from "src/components/simple-dialog";
import DisplayErrors from "src/components/display-errors";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxCheckboxField from "src/components/inputs/trux-checkbox-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { Constants } from "src/constants";
import { TruxTableCell } from "src/components/trux-table-cell";
import { TruxDeleteTableCell } from "src/components/trux-delete-table-cell";
import { SmallTab } from "src/components/small-tab";

type PageModel = {
  eldOrganizationSettings?: EldOrganizationSettings,
  organization?: Organization,
}

export default function Page() {
  const settings = useSettingsContext();

  const dataLoader = useCallback(() => ({
    eldOrganizationSettings: axios.get(endpoints.eldOrganizationSettings.current).then(response => response.data),
    organization: axios.get(endpoints.organizations.current).then(response => response.data),
  }), []);

  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const previousEldOrganizationSettings = usePrevious(data?.eldOrganizationSettings)
  const previousOrganization = usePrevious(data?.organization)

  const eldOrganizationSettingsModel = useModelSavingData<EldOrganizationSettings>(
    endpoints.eldOrganizationSettings.update
  );

  const organizationModel = useModelSavingData<Organization>(
    endpoints.organizations.update
  );

  useEffect(() => {
    if (data?.eldOrganizationSettings && data.eldOrganizationSettings !== previousEldOrganizationSettings) {
      eldOrganizationSettingsModel.set(data.eldOrganizationSettings)
    }
  }, [previousEldOrganizationSettings, data?.eldOrganizationSettings, eldOrganizationSettingsModel]);

  useEffect(() => {
    if (data?.organization && data.organization !== previousOrganization) {
      organizationModel.set(data.organization)
    }
  }, [previousOrganization, data?.organization, organizationModel]);

  const [isOpenSaveChanges, setIsOpenSaveChanges] = useState(false);
  const [savingFile, setSavingFile] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach(file => {
        setSavingFile(true)

        axios.postForm(
          endpoints.organizations.uploadFile,
          { file }
        ).then(response => response.data).then((response) => {
          if (response.fileUrl) {
            enqueueSnackbar("Successfully uploaded the Company Logo", { variant: "success" });

            organizationModel.update(organization => {
              organization.companyLogoUrl = response.fileUrl;
            });
          }
        }).catch(() => {
          enqueueSnackbar("Your file could not be uploaded. Please try again.", { variant: "error" });
        }).finally(() => {
          setSavingFile(false);
        })
      })
    },
    [organizationModel]
  );

  const onRemoveFile = useCallback(() => {
    organizationModel.update(organization => {
      organization.companyLogoUrl = "";
    })
  }, [organizationModel]);

  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    onDrop, accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
      'image/jpeg': ['.jpeg'],
    }
  })

  const updateEldOrganizationSettings = useCallback(async () => {
    // Save both eldOrganizationSettings and organization
    await eldOrganizationSettingsModel.save().then((response) => {
      if (response) {
        updateData("eldOrganizationSettings")(response.data as any)
      }
    }).catch(() => {
      enqueueSnackbar("Failed to update settings. Please try again.", { variant: "error" });
      return;
    })
    await organizationModel.save().then((response) => {
      if (response) {
        updateData("organization")(response.data as any)
        enqueueSnackbar("Successfully updated the Organization Settings", { variant: "success" })
        setIsOpenSaveChanges(false);
      }
    }).catch(() => {
      enqueueSnackbar("Failed to update company logo. Please try again.", { variant: "error" });
    })
  }, [eldOrganizationSettingsModel, organizationModel, updateData]);

  const [selectedTab, setSelectedTab] = useState<"general"|"notifications">("general");
  
  const updateEldOrganizationSettingsButton = <Button
    onClick={() => setIsOpenSaveChanges(true)}
    variant="outlined"
    color="info"
    startIcon={<Iconify icon="basil:save-solid" />}
  >
    Save Changes
  </Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {eldOrganizationSettingsModel.model && organizationModel.model && <Box sx={{ display: "flex", flexGrow: 1 }}>

          <Stack direction="column" flexGrow={1} gap={2}>
            <PageHeader title="Settings">
              {updateEldOrganizationSettingsButton}
            </PageHeader>

            <Tabs
              value={selectedTab}
              onChange={(_, value) => setSelectedTab(value)}
              sx={{
                mt: -3,
                minHeight: "auto"
              }}
            >
              {['general', 'notifications'].map((tab) => (
                <SmallTab
                  key={tab}
                  iconPosition="end"
                  value={tab}
                  label={tab}
                  sx={{ textTransform: 'capitalize' }}
                />))}
            </Tabs>

            <Card>
              {selectedTab === "general" && (
                <CardContent>
                  <Stack spacing={2} sx={{ p: 2 }}>
                    <TruxFormField label="Company Name *">
                      <TruxTextField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="companyName"
                      />
                    </TruxFormField>

                    <TruxFormField label="USDOT *">
                      <TruxTextField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="dotNumber"
                      />
                    </TruxFormField>

                    <TruxFormField label="TimeZone *">
                      <TruxSelectField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="companyTimeZone"
                      >
                        {Constants.TIMEZONES.map((tz, i) => <MenuItem value={tz} key={i}>{tz}</MenuItem>)}
                      </TruxSelectField>
                    </TruxFormField>

                    <TruxFormField label="Company Address">
                      <Stack spacing={1}>
                        <Box display="flex" gap={2}>
                          <Box flexGrow={1}>
                            <TruxSimpleTextField label="Street 1" value={eldOrganizationSettingsModel.model.companyAddress.street1} onChange={(val) => eldOrganizationSettingsModel.update((model) => {
                              model.companyAddress.street1 = val;
                            })} />
                          </Box>
                          <Box flexGrow={1}>
                            <TruxSimpleTextField label="Street 2" value={eldOrganizationSettingsModel.model.companyAddress.street2} onChange={(val) => eldOrganizationSettingsModel.update((model) => {
                              model.companyAddress.street2 = val;
                            })} />
                          </Box>
                        </Box>
                        <Box display="flex" gap={2}>
                          <Box flexGrow={1}>
                            <TruxSimpleTextField label="City" value={eldOrganizationSettingsModel.model.companyAddress.city} onChange={(val) => eldOrganizationSettingsModel.update((model) => {
                              model.companyAddress.city = val;
                            })} />
                          </Box>
                          <Box flexGrow={1}>
                            <TruxSimpleSelectField value={eldOrganizationSettingsModel.model.companyAddress.state} onChange={(val) => eldOrganizationSettingsModel.update((model) => {
                              model.companyAddress.state = val;
                            })}>
                              <MenuItem value="" selected={!!eldOrganizationSettingsModel.model.companyAddress.state}>Please Select</MenuItem>
                              {Constants.STATES.map((state) => <MenuItem value={state} key={state} selected={state === eldOrganizationSettingsModel?.model?.companyAddress.state}>{state}</MenuItem>)}
                            </TruxSimpleSelectField>
                          </Box>
                          <Box flexGrow={1}>
                            <TruxSimpleTextField label="Zip" value={eldOrganizationSettingsModel.model.companyAddress.zip} onChange={(val) => eldOrganizationSettingsModel.update((model) => {
                              model.companyAddress.zip = val;
                            })} />
                          </Box>
                        </Box>
                      </Stack>
                    </TruxFormField>


                    <TruxFormField label="Motion Threshold">
                      <TruxTextField
                        disabled
                        modelSavingData={eldOrganizationSettingsModel}
                        field="vehicleMotionThreshold"
                      />
                      <Typography fontSize="small" fontStyle="italic">
                        * miles per hour
                      </Typography>
                    </TruxFormField>

                    {eldOrganizationSettingsModel.model?.apiToken &&
                      <TruxFormField label="API Token">
                        <TruxTextField
                          disabled
                          modelSavingData={eldOrganizationSettingsModel}
                          field="apiToken"
                        />
                      </TruxFormField>
                    }


                    {/* <TruxFormField label="Cycle Rule">
                      <TruxTextField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="cycleRule"
                      />
                    </TruxFormField>

                    <TruxFormField label="Cargo Type">
                      <TruxTextField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="cargoType"
                      />
                    </TruxFormField>


                    <TruxFormField label="Rest Break">
                      <TruxTextField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="restBreak"
                      />
                    </TruxFormField> */}




                    {/* <TruxFormField label="Short Haul Exception">
                      <TruxCheckboxField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="shortHaulException"
                      />
                    </TruxFormField> */}
                    <TruxFormField label="Personal Conveyance">
                      <TruxCheckboxField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="personalConveyance"
                      />
                    </TruxFormField>
                    <TruxFormField label="Yard Moves">
                      <TruxCheckboxField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="yardMoves"
                      />
                    </TruxFormField>
                    {/* <TruxFormField label="Exempt Driver">
                      <TruxCheckboxField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="exemptDriver"
                      />
                    </TruxFormField> */}
                    <TruxFormField label="Keep Shipping Docs">
                      <TruxCheckboxField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="keepShippingDocs"
                      />
                    </TruxFormField>
                    <TruxFormField label="Keep Co-Drivers">
                      <TruxCheckboxField
                        modelSavingData={eldOrganizationSettingsModel}
                        field="keepCoDrivers"
                      />
                    </TruxFormField>

                    <TruxFormField label="Company Logo">
                      <>
                        {savingFile && <CircularProgress />}
                        {!savingFile && organizationModel.model?.companyLogoUrl && <Stack direction="row" gap={2}>
                          <Box maxWidth="200px">
                            <img src={organizationModel.model?.companyLogoUrl} alt="Company Logo" style={{ maxWidth: "100%", height: "auto" }} />
                          </Box>
                          <Box>
                            <IconButton color="error" onClick={onRemoveFile} >
                              <Iconify icon="solar:trash-bin-trash-bold" />
                            </IconButton>
                          </Box>
                        </Stack>}
                        {!savingFile && !organizationModel.model?.companyLogoUrl && <Box>
                          <Box {...getRootProps()} onFocus={null as any} onBlur={null as any}>
                            <input {...getInputProps()} />

                            <Box sx={{
                              m: 0.5,
                              flexShrink: 0,
                              flexGrow: 1,
                              height: "80px",
                              width: "120px",
                              borderRadius: 1,
                              cursor: 'pointer',
                              alignItems: 'center',
                              color: 'text.disabled',
                              justifyContent: 'center',
                              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                              border: (theme) => `dashed 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                              ...(isDragActive && {
                                opacity: 0.72,
                              }),
                              '&:hover': {
                                opacity: 0.72,
                              }
                            }}><Stack spacing={0.5} paddingTop={1} alignItems="center">
                                <Iconify icon="eva:cloud-upload-fill" width={40} />
                                <Typography variant="body2">Upload Logo</Typography>
                              </Stack></Box>
                          </Box>
                        </Box>}
                      </>
                    </TruxFormField>
                  </Stack>
                </CardContent>
              )}
              {selectedTab === "notifications" && <Stack>
                <Box>
                  <TableContainer sx={{ maxHeight: 440 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {(eldOrganizationSettingsModel.model.inspectionNotificationEmails?.length || 0) > 1 && <TruxTableCell sx={{ maxWidth: "30px" }} />}
                          <TruxTableCell>
                            Email
                          </TruxTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(eldOrganizationSettingsModel.model.inspectionNotificationEmails || []).map((email, index) => <TableRow key={index}>
                          {(eldOrganizationSettingsModel?.model?.inspectionNotificationEmails.length || 0) > 1 &&
                            <TruxDeleteTableCell>
                              <IconButton color="error" onClick={() => {
                                eldOrganizationSettingsModel.update((model) => {
                                  if (!model.inspectionNotificationEmails) {
                                    model.inspectionNotificationEmails = [];
                                  }
                                  model.inspectionNotificationEmails.splice(index, 1);
                                })
                              }}>
                                <Iconify icon="solar:trash-bin-trash-bold" />
                              </IconButton>
                            </TruxDeleteTableCell>
                          }
                          <TruxTableCell>
                            <TruxSimpleTextField value={email} onChange={(val) => {
                              eldOrganizationSettingsModel.update((model) => {
                                if (!model.inspectionNotificationEmails) {
                                  model.inspectionNotificationEmails = [];
                                }
                                model.inspectionNotificationEmails[index] = val;
                              })
                            }} />
                          </TruxTableCell>
                        </TableRow>)}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box sx={{ padding: "12px" }}>
                  <Button size="small" variant="soft" onClick={() => {
                    eldOrganizationSettingsModel.update((model) => {
                      if (!model.inspectionNotificationEmails) {
                        model.inspectionNotificationEmails = [];
                      }
                      model.inspectionNotificationEmails.push("");
                    })
                  }} startIcon={<Iconify icon="mingcute:add-line" />}>
                    Add Email
                  </Button>
                </Box>
              </Stack>}
            </Card>
            <Typography sx={{display:"flex", justifyContent:"center", fontWeight:"450", fontStyle:"italic"}}>
              Add emails to receive real-time notifications about new DOT Inspections.
            </Typography>
          </Stack>
        </Box>}
      </DisplayPageData >

      {eldOrganizationSettingsModel.model && organizationModel.model &&
        <SimpleDialog
          title="Save Organization Settings"
          open={isOpenSaveChanges}
          onCancel={() => {
            setIsOpenSaveChanges(false);
            eldOrganizationSettingsModel.clearErrors();
            organizationModel.clearErrors();
          }}
          onSubmit={updateEldOrganizationSettings}
          submitDisabled={eldOrganizationSettingsModel.loading || organizationModel.loading}
          submitButton="Save Organization"
          submitButtonIcon={<Iconify icon="basil:save-solid" />}
          submitButtonColor="info">

          <DisplayErrors savingData={eldOrganizationSettingsModel} />
          <DisplayErrors savingData={organizationModel} />

          Are you sure you would like to save your changes?
        </SimpleDialog>}
    </Container >
  );
}
